import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

interface ContractorType {
  _id: string;
  name: string;
  slug: string;
  icon: string;
  description?: string;
  isActive: boolean;
  isSystemDefined: boolean;
  createdAt: string;
  updatedAt: string;
}

// Common contractor-related emojis
const EMOJI_OPTIONS = [
  { emoji: '📸', label: 'Camera (Photography)' },
  { emoji: '🎨', label: 'Artist Palette (Styling)' },
  { emoji: '🔧', label: 'Wrench (Handyman)' },
  { emoji: '🔨', label: 'Hammer (Construction)' },
  { emoji: '💡', label: 'Light Bulb (Electrician)' },
  { emoji: '🚿', label: 'Shower (Plumber)' },
  { emoji: '🪜', label: 'Ladder (Maintenance)' },
  { emoji: '🎭', label: 'Theatre Masks (Interior Design)' },
  { emoji: '🌿', label: 'Herb (Landscaping)' },
  { emoji: '🪴', label: 'Plant (Gardening)' },
  { emoji: '🧹', label: 'Broom (Cleaning)' },
  { emoji: '🪟', label: 'Window (Window Cleaning)' },
  { emoji: '🏡', label: 'House (General Contractor)' },
  { emoji: '🔑', label: 'Key (Locksmith)' },
  { emoji: '🎬', label: 'Clapper Board (Videography)' },
  { emoji: '📐', label: 'Triangular Ruler (Architecture)' },
  { emoji: '🖌️', label: 'Paintbrush (Painting)' },
  { emoji: '🪛', label: 'Screwdriver (Repairs)' },
  { emoji: '⚡', label: 'Lightning (Electrical)' },
  { emoji: '💧', label: 'Droplet (Plumbing)' },
  { emoji: '🌡️', label: 'Thermometer (HVAC)' },
  { emoji: '🔒', label: 'Lock (Security)' },
  { emoji: '📦', label: 'Package (Moving)' },
  { emoji: '🧰', label: 'Toolbox (General Services)' },
  { emoji: '👷', label: 'Construction Worker (Default)' },
];

export const ContractorTypeManagement: React.FC = () => {
  const { agency } = useAuth();
  const [types, setTypes] = useState<ContractorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingType, setEditingType] = useState<ContractorType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '👷',
    description: '',
  });

  useEffect(() => {
    if (agency?.slug) {
      fetchTypes();
    }
  }, [agency?.slug]);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/agencies/${agency?.slug}/contractor-types`);
      setTypes(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contractor types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type?: ContractorType) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        icon: type.icon,
        description: type.description || '',
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        icon: '👷',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingType(null);
    setFormData({
      name: '',
      icon: '👷',
      description: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }

      if (editingType) {
        // Update existing type
        await api.put(
          `/agencies/${agency?.slug}/contractor-types/${editingType._id}`,
          formData
        );
        setSuccess('Contractor type updated successfully');
      } else {
        // Create new type
        await api.post(
          `/agencies/${agency?.slug}/contractor-types`,
          formData
        );
        setSuccess('Contractor type created successfully');
      }

      handleCloseDialog();
      fetchTypes();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save contractor type');
    }
  };

  const handleDelete = async (typeId: string) => {
    if (!window.confirm('Are you sure you want to delete this contractor type?')) {
      return;
    }

    try {
      await api.delete(`/agencies/${agency?.slug}/contractor-types/${typeId}`);
      setSuccess('Contractor type deleted successfully');
      fetchTypes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete contractor type');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Contractor Types</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Type
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Define the types of contractors you work with (e.g., Photographer, Styler, Handyman).
            These types will be available when assigning contractors to properties.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Icon</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : types.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No contractor types found. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  types.map((type) => (
                    <TableRow key={type._id}>
                      <TableCell>
                        <Typography variant="h6">{type.icon}</Typography>
                      </TableCell>
                      <TableCell>{type.name}</TableCell>
                      <TableCell>{type.description || '-'}</TableCell>
                      <TableCell>
                        {type.isSystemDefined ? (
                          <Chip label="System" size="small" color="primary" />
                        ) : (
                          <Chip label="Custom" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!type.isSystemDefined && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(type)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(type._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {type.isSystemDefined && (
                          <Typography variant="caption" color="text.secondary">
                            System type
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType ? 'Edit Contractor Type' : 'Add Contractor Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Handyman, Electrician, Plumber"
            />

            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                label="Icon"
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{value}</Typography>
                    <Typography variant="body2">
                      {EMOJI_OPTIONS.find(opt => opt.emoji === value)?.label || 'Custom'}
                    </Typography>
                  </Box>
                )}
              >
                {EMOJI_OPTIONS.map((option) => (
                  <MenuItem key={option.emoji} value={option.emoji}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="h6" sx={{ minWidth: 40 }}>{option.emoji}</Typography>
                      <Typography variant="body2">{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Brief description of this contractor type's services"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

